import {memo, type ReactNode, useState, useCallback} from "react";
import {Box, Flex, HStack, Stack, Text} from "@chakra-ui/react";
import {SparklesIcon, PencilSquareIcon, CheckIcon, CheckCircleIcon} from "@heroicons/react/16/solid";
import {Type} from "typebox";
import {Button} from "../../../../../components/ui/button";
import {
  PopoverRoot,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverCloseTrigger,
} from "../../../../../components/ui/popover";
import {Checkbox} from "../../../../../components/ui/checkbox";
import {AutoResizingTextarea} from "../../../../../components/AutoResizingTextarea";
import {useLocalState} from "../../../../../hooks/localState";
import type {EditMeta, MockEvidence} from "../types";
import {formatRelativeTime} from "../types";
import {CitationBadge} from "./ProcessEvidenceActivity";
import {CompactMarkdown} from "./CompactMarkdown";

const AttestationSchema = Type.Union([
  Type.Object({attestedBy: Type.String(), attestedAt: Type.String()}),
  Type.Null(),
]);

export const AiProvenance = memo(
  ({
    contentKey,
    meta,
    citations,
    onCitationClick,
  }: {
    contentKey: string;
    meta: EditMeta;
    citations?: MockEvidence[];
    onCitationClick?: (e: MockEvidence) => void;
  }) => {
    const [attestation, setAttestation] = useLocalState(`mockup:attestation:${contentKey}`, AttestationSchema, null);
    const isAi = meta.editedBy === "ai";

    const handleToggleAttestation = useCallback(() => {
      if (attestation) {
        setAttestation(null);
      } else {
        setAttestation({attestedBy: "Henry Irish", attestedAt: new Date().toISOString()});
      }
    }, [attestation, setAttestation]);

    const attestationBadge = !isAi ? null : !attestation ? (
      <PopoverRoot>
        <PopoverTrigger asChild>
          <HStack
            as="button"
            gap={1}
            px={1.5}
            py={0.5}
            borderRadius="full"
            bg="orange.50"
            border="1px solid {colors.orange.200}"
            cursor="pointer"
            _hover={{bg: "orange.100"}}
          >
            <Box boxSize="6px" borderRadius="full" bg="orange.400" />
            <Text fontSize="xs" color="orange.700" fontWeight="medium">
              Needs review
            </Text>
          </HStack>
        </PopoverTrigger>
        <PopoverContent w="xs">
          <PopoverCloseTrigger />
          <PopoverBody py={3}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
              Human Review
            </Text>
            <Checkbox checked={false} onCheckedChange={handleToggleAttestation} size="sm">
              <Text fontSize="sm">I have reviewed this content</Text>
            </Checkbox>
          </PopoverBody>
        </PopoverContent>
      </PopoverRoot>
    ) : (
      <PopoverRoot>
        <PopoverTrigger asChild>
          <HStack
            as="button"
            gap={1}
            px={1.5}
            py={0.5}
            borderRadius="full"
            bg="green.50"
            border="1px solid {colors.green.200}"
            cursor="pointer"
            _hover={{bg: "green.100"}}
          >
            <Box as={CheckCircleIcon} boxSize="12px" color="green.500" />
            <Text fontSize="xs" color="green.700" fontWeight="medium">
              Reviewed
            </Text>
          </HStack>
        </PopoverTrigger>
        <PopoverContent w="xs">
          <PopoverCloseTrigger />
          <PopoverBody py={3}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
              Human Review
            </Text>
            <Checkbox checked onCheckedChange={handleToggleAttestation} size="sm">
              <Text fontSize="sm">I have reviewed this content</Text>
            </Checkbox>
            <Stack gap={0.5} mt={2} pl={6}>
              <Text fontSize="xs" color="gray.500">
                Reviewed by {attestation.attestedBy}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {new Date(attestation.attestedAt).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
                , {new Date(attestation.attestedAt).toLocaleTimeString("en-GB", {hour: "2-digit", minute: "2-digit"})}
              </Text>
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </PopoverRoot>
    );

    return (
      <HStack gap={1.5} mt={2} px={1} flexWrap="wrap">
        <HStack gap={1.5} minW={0}>
          <Box as={isAi ? SparklesIcon : PencilSquareIcon} boxSize="12px" flexShrink={0} color="gray.500" />
          <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
            {isAi ? "Generated by AI" : "Edited by you"} · {formatRelativeTime(meta.editedAt)}
          </Text>
        </HStack>
        {citations && citations.length > 0 && onCitationClick && (
          <HStack gap={0.5} flexWrap="wrap">
            {citations.map(ev => (
              <CitationBadge key={ev.id} citationId={ev.citationId} onClick={() => onCitationClick(ev)} />
            ))}
          </HStack>
        )}
        {attestationBadge}
      </HStack>
    );
  },
);

type EntitySummaryProps = {contentKey: string; header?: ReactNode; initialContent: string; footer?: ReactNode};

export const EntitySummary = memo(({contentKey, header, initialContent, footer}: EntitySummaryProps) => {
  const [content, setContent] = useState(initialContent);
  const [editMeta, setEditMeta] = useState<EditMeta>({editedBy: "ai", editedAt: new Date()});
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const handleStartEdit = useCallback(() => {
    setDraft(content);
    setIsEditing(true);
  }, [content]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSaveEdit = useCallback(() => {
    setContent(draft);
    setEditMeta({editedBy: "human", editedAt: new Date()});
    setIsEditing(false);
  }, [draft]);

  return (
    <Flex direction="column" minH={0} flex={1}>
      {header && header}

      <Stack gap={6} p={6} bg="gray.50" flex={1} overflowY="auto">
        <Box>
          <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" mb={3}>
            Audit Strategy
          </Text>
          <Box bg="white" borderRadius="md" border="1px solid {colors.gray.200}" p={4} position="relative">
            {isEditing ? (
              <Stack gap={3}>
                <AutoResizingTextarea
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  fontSize="sm"
                  minRows={8}
                  placeholder="Write markdown here..."
                />
                <HStack gap={2}>
                  <Button size="xs" colorPalette="blue" onClick={handleSaveEdit}>
                    <CheckIcon />
                    Save
                  </Button>
                  <Button size="xs" variant="ghost" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                </HStack>
              </Stack>
            ) : (
              <>
                <Box
                  as="button"
                  position="absolute"
                  top={3}
                  right={3}
                  p={1}
                  borderRadius="sm"
                  color="gray.400"
                  _hover={{color: "blue.500", bg: "gray.50"}}
                  cursor="pointer"
                  onClick={handleStartEdit}
                  title="Edit summary"
                >
                  <Box as={PencilSquareIcon} boxSize="14px" />
                </Box>
                <Box pr={6}>
                  <CompactMarkdown>{content}</CompactMarkdown>
                </Box>
              </>
            )}
          </Box>
          <AiProvenance contentKey={contentKey} meta={editMeta} />
        </Box>
        {footer}
      </Stack>
    </Flex>
  );
});
